import React, { useEffect, useState, Children } from 'react';
import { VendorDashboard } from './VendorDashboard';
import { VendorList } from './VendorList';
import { VendorOnboarding } from './VendorOnboarding';
import { VendorDetails } from './VendorDetails';
import { ComplianceCheck } from './ComplianceCheck';
import { RegistrationsManagement } from './registrations/RegistrationsManagement';
import { VendorLookupModal } from './VendorLookupModal';
import { sampleVendorAccounts } from './sharedVendorData';
export function VendorManagement({
  locale = 'en-US',
  currentView: parentView
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showLookup, setShowLookup] = useState(false);
  const [currentView, setCurrentView] = useState(parentView || 'dashboard');
  const [selectedVendor, setSelectedVendor] = useState(null);
  useEffect(() => {
    if (parentView && parentView !== 'vendor-management') {
      const viewName = parentView.replace('vendor-', '');
      setCurrentView(viewName);
    }
  }, [parentView]);
  // OrgUnit data structure
  const orgUnits = [{
    id: 'org-001',
    code: 'GLOBAL',
    name: 'Global Technologies Inc.',
    orgUnitType: {
      name: 'Legal Entity',
      code: 'ENTITY'
    },
    path: '/GLOBAL',
    depth: 0
  }, {
    id: 'org-002',
    code: 'NY-HQ',
    name: 'New York Headquarters',
    orgUnitType: {
      name: 'Physical Site',
      code: 'SITE'
    },
    parentOrgUnitId: 'org-001',
    path: '/GLOBAL/NY-HQ',
    depth: 1
  }, {
    id: 'org-005',
    code: 'NY-FIN',
    name: 'Finance Department',
    orgUnitType: {
      name: 'Department',
      code: 'DEPT'
    },
    parentOrgUnitId: 'org-002',
    path: '/GLOBAL/NY-HQ/NY-FIN',
    depth: 2
  }, {
    id: 'org-003',
    code: 'SF-OFFICE',
    name: 'San Francisco Office',
    orgUnitType: {
      name: 'Physical Site',
      code: 'SITE'
    },
    parentOrgUnitId: 'org-001',
    path: '/GLOBAL/SF-OFFICE',
    depth: 1
  }, {
    id: 'org-004',
    code: 'EURO-OPS',
    name: 'European Operations Ltd',
    orgUnitType: {
      name: 'Legal Entity',
      code: 'ENTITY'
    },
    path: '/EURO-OPS',
    depth: 0
  }, {
    id: 'org-006',
    code: 'APAC-PTY',
    name: 'Asia Pacific Technologies Pty Ltd',
    orgUnitType: {
      name: 'Legal Entity',
      code: 'ENTITY'
    },
    path: '/APAC-PTY',
    depth: 0
  }, {
    id: 'org-007',
    code: 'SYD-OFFICE',
    name: 'Sydney Office',
    orgUnitType: {
      name: 'Physical Site',
      code: 'SITE'
    },
    parentOrgUnitId: 'org-006',
    path: '/APAC-PTY/SYD-OFFICE',
    depth: 1
  }];
  const vendorStatuses = [{
    id: 'status-001',
    code: 'ACTIVE',
    name: 'Active',
    isSystem: true
  }, {
    id: 'status-002',
    code: 'PENDING',
    name: 'Pending Onboarding',
    isSystem: true
  }, {
    id: 'status-003',
    code: 'INACTIVE',
    name: 'Inactive',
    isSystem: true
  }, {
    id: 'status-004',
    code: 'INVITED',
    name: 'Invited',
    isSystem: true
  }, {
    id: 'status-005',
    code: 'SUSPENDED',
    name: 'Suspended',
    isSystem: true
  }];
  const paymentTerms = [{
    id: 'term-001',
    code: 'NET_7',
    name: 'Net 7 days',
    daysFromInvoiceDate: 7,
    isSystem: true
  }, {
    id: 'term-002',
    code: 'NET_14',
    name: 'Net 14 days',
    daysFromInvoiceDate: 14,
    isSystem: true
  }, {
    id: 'term-003',
    code: 'NET_30',
    name: 'Net 30 days',
    daysFromInvoiceDate: 30,
    isSystem: true
  }, {
    id: 'term-004',
    code: 'NET_60',
    name: 'Net 60 days',
    daysFromInvoiceDate: 60,
    isSystem: true
  }, {
    id: 'term-005',
    code: 'EOM',
    name: 'End of Month',
    isEndOfMonth: true,
    isSystem: true
  }, {
    id: 'term-006',
    code: 'EOM_30',
    name: 'End of Month + 30',
    isEndOfMonth: true,
    daysAfterEndOfMonth: 30,
    isSystem: true
  }];
  const complianceSeverities = [{
    id: 'sev-001',
    code: 'INFO',
    name: 'Information',
    isBlocking: false,
    sortOrder: 1
  }, {
    id: 'sev-002',
    code: 'WARNING',
    name: 'Warning',
    isBlocking: false,
    sortOrder: 2
  }, {
    id: 'sev-003',
    code: 'HIGH',
    name: 'High',
    isBlocking: true,
    sortOrder: 3
  }, {
    id: 'sev-004',
    code: 'CRITICAL',
    name: 'Critical',
    isBlocking: true,
    sortOrder: 4
  }];
  const complianceIssueTypes = [{
    id: 'type-001',
    code: 'BANK_VERIFICATION',
    name: 'Bank Verification Failed'
  }, {
    id: 'type-002',
    code: 'EXPIRED_DOCUMENT',
    name: 'Expired Document'
  }, {
    id: 'type-003',
    code: 'MISSING_DOCUMENT',
    name: 'Missing Required Document'
  }, {
    id: 'type-004',
    code: 'BACKGROUND_CHECK',
    name: 'Background Check Issue'
  }];
  const complianceIssueStatuses = [{
    id: 'status-001',
    code: 'OPEN',
    name: 'Open',
    isTerminal: false
  }, {
    id: 'status-002',
    code: 'ACKNOWLEDGED',
    name: 'Acknowledged',
    isTerminal: false
  }, {
    id: 'status-003',
    code: 'RESOLVED',
    name: 'Resolved',
    isTerminal: true
  }, {
    id: 'status-004',
    code: 'WAIVED',
    name: 'Waived',
    isTerminal: true
  }];
  const generateVendors = () => {
    const vendorNames = [{
      name: 'Office Depot',
      code: 'OD-001',
      type: 'Supplier',
      country: 'United States',
      orgUnitId: null,
      includeChildren: false,
      contactIds: ['con-005', 'con-009'],
      bankAccountIds: ['ba-005'],
      addressIds: ['addr-005']
    }, {
      name: 'Dell Technologies',
      code: 'DELL-001',
      type: 'IT Equipment',
      country: 'United States',
      orgUnitId: 'org-002',
      includeChildren: true,
      contactIds: ['con-006', 'con-010'],
      bankAccountIds: ['ba-004'],
      addressIds: ['addr-006']
    }, {
      name: 'PrintWorks Inc.',
      code: 'PW-001',
      type: 'Printing Services',
      country: 'Canada',
      orgUnitId: 'org-005',
      includeChildren: false,
      contactIds: ['con-007'],
      bankAccountIds: ['ba-006'],
      addressIds: ['addr-007']
    }, {
      name: 'Acme Construction',
      code: 'ACM-001',
      type: 'Contractor',
      country: 'United States',
      orgUnitId: 'org-003',
      includeChildren: false,
      contactIds: ['con-008'],
      bankAccountIds: ['ba-007'],
      addressIds: ['addr-008']
    }, {
      name: 'Global Logistics Partners',
      code: 'GLP-001',
      type: 'Distributor',
      country: 'United Kingdom',
      orgUnitId: null,
      includeChildren: false,
      contactIds: ['con-016'],
      bankAccountIds: ['ba-010'],
      addressIds: ['addr-003']
    }, {
      name: 'TechSupply Co.',
      code: 'TSC-001',
      type: 'Supplier',
      country: 'Germany',
      orgUnitId: 'org-004',
      includeChildren: true,
      contactIds: ['con-015'],
      bankAccountIds: ['ba-009'],
      addressIds: ['addr-010']
    }, {
      name: 'Green Energy Solutions',
      code: 'GES-001',
      type: 'Service Provider',
      country: 'United States',
      orgUnitId: 'org-001',
      includeChildren: true,
      contactIds: ['con-017'],
      bankAccountIds: ['ba-011'],
      addressIds: ['addr-011']
    }, {
      name: 'Pacific Manufacturing',
      code: 'PMF-001',
      type: 'Manufacturer',
      country: 'Japan',
      orgUnitId: 'org-006',
      includeChildren: true,
      contactIds: ['con-011'],
      bankAccountIds: ['ba-008'],
      addressIds: ['addr-009']
    }, {
      name: 'Metro Cleaning Services',
      code: 'MCS-001',
      type: 'Service Provider',
      country: 'United States',
      orgUnitId: 'org-002',
      includeChildren: false,
      contactIds: ['con-018'],
      bankAccountIds: ['ba-012'],
      addressIds: ['addr-014']
    }, {
      name: 'Elite Consulting Group',
      code: 'ECG-001',
      type: 'Consultant',
      country: 'United States',
      orgUnitId: null,
      includeChildren: false,
      contactIds: ['con-019'],
      bankAccountIds: ['ba-013'],
      addressIds: ['addr-015']
    }, {
      name: 'Northern Supplies Ltd',
      code: 'NSL-001',
      type: 'Supplier',
      country: 'Canada',
      orgUnitId: 'org-007',
      includeChildren: false,
      contactIds: ['con-020'],
      bankAccountIds: ['ba-014'],
      addressIds: ['addr-016']
    }, {
      name: 'Apex Security Systems',
      code: 'ASS-001',
      type: 'Service Provider',
      country: 'United States',
      orgUnitId: 'org-001',
      includeChildren: true,
      contactIds: ['con-021'],
      bankAccountIds: ['ba-015'],
      addressIds: ['addr-017']
    }, {
      name: 'Continental Foods',
      code: 'CFD-001',
      type: 'Supplier',
      country: 'France',
      orgUnitId: 'org-004',
      includeChildren: false,
      contactIds: ['con-022'],
      bankAccountIds: ['ba-016'],
      addressIds: ['addr-018']
    }, {
      name: 'Digital Marketing Pro',
      code: 'DMP-001',
      type: 'Service Provider',
      country: 'United Kingdom',
      orgUnitId: null,
      includeChildren: false,
      contactIds: ['con-012'],
      bankAccountIds: ['ba-017'],
      addressIds: ['addr-012']
    }, {
      name: 'Industrial Parts Warehouse',
      code: 'IPW-001',
      type: 'Distributor',
      country: 'United States',
      orgUnitId: 'org-003',
      includeChildren: false,
      contactIds: ['con-023'],
      bankAccountIds: ['ba-018'],
      addressIds: ['addr-019']
    }, {
      name: 'Swift Transportation',
      code: 'SWT-001',
      type: 'Service Provider',
      country: 'United States',
      orgUnitId: 'org-002',
      includeChildren: true,
      contactIds: ['con-024'],
      bankAccountIds: ['ba-019'],
      addressIds: ['addr-020']
    }, {
      name: 'Premium Office Furniture',
      code: 'POF-001',
      type: 'Supplier',
      country: 'Italy',
      orgUnitId: 'org-004',
      includeChildren: true,
      contactIds: ['con-025'],
      bankAccountIds: ['ba-020'],
      addressIds: ['addr-021']
    }, {
      name: 'CloudTech Solutions',
      code: 'CTS-001',
      type: 'Service Provider',
      country: 'United States',
      orgUnitId: 'org-005',
      includeChildren: false,
      contactIds: ['con-026'],
      bankAccountIds: ['ba-021'],
      addressIds: ['addr-022']
    }, {
      name: 'Reliable Maintenance Co.',
      code: 'RMC-001',
      type: 'Contractor',
      country: 'United States',
      orgUnitId: 'org-006',
      includeChildren: false,
      contactIds: ['con-027'],
      bankAccountIds: ['ba-022'],
      addressIds: ['addr-023']
    }, {
      name: 'Global Trade Partners',
      code: 'GTP-001',
      type: 'Distributor',
      country: 'China',
      orgUnitId: null,
      includeChildren: false,
      contactIds: ['con-028'],
      bankAccountIds: ['ba-023'],
      addressIds: ['addr-024']
    }, {
      name: 'Melbourne Office Solutions Pty Ltd',
      code: 'MOS-001',
      type: 'Supplier',
      country: 'Australia',
      orgUnitId: 'org-006',
      includeChildren: true,
      contactIds: ['con-029', 'con-030'],
      bankAccountIds: ['ba-024'],
      addressIds: ['addr-025', 'addr-026']
    }];
    // Helper function to generate registrations based on country
    const generateRegistrations = (country, vendorIndex) => {
      const baseDate = new Date(2020, 0, 1);
      const registrations = [];
      switch (country) {
        case 'United States':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${20 + vendorIndex}-${String(1234567 + vendorIndex * 111).padStart(7, '0')}`,
            schemeCode: 'EIN',
            issuer: 'Internal Revenue Service',
            country: 'US',
            countryId: 'country-US',
            countryName: 'United States',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: [{
              registrationAttributeId: `attr-${vendorIndex}-001-001`,
              attributeKey: 'Business Type',
              attributeValue: 'Corporation',
              valueType: 'string',
              source: 'IRS',
              effectiveFrom: baseDate.toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-002`,
              attributeKey: 'Tax Classification',
              attributeValue: 'C-Corp',
              valueType: 'string',
              source: 'IRS',
              effectiveFrom: baseDate.toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }]
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `${String(87654321 + vendorIndex * 1234).padStart(8, '0')}`,
            schemeCode: 'DUNS',
            issuer: 'Dun & Bradstreet',
            country: 'US',
            countryId: 'country-US',
            countryName: 'United States',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: [{
              registrationAttributeId: `attr-${vendorIndex}-002-001`,
              attributeKey: 'Credit Rating',
              attributeValue: 'AAA',
              valueType: 'string',
              source: 'Dun & Bradstreet',
              effectiveFrom: new Date(2023, 0, 1).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }]
          });
          break;
        case 'Australia':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: '51824753556',
            schemeCode: 'ABN',
            issuer: 'Australian Business Register',
            country: 'AU',
            countryId: 'country-AU',
            countryName: 'Australia',
            effectiveFrom: new Date(2018, 5, 15).toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: new Date(2018, 5, 15).toISOString(),
            createdById: 'system',
            attributes: [{
              registrationAttributeId: `attr-${vendorIndex}-001-001`,
              attributeKey: 'Entity Name',
              attributeValue: 'Melbourne Office Solutions Pty Ltd',
              valueType: 'string',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-002`,
              attributeKey: 'ABN Status',
              attributeValue: 'Active',
              valueType: 'string',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-003`,
              attributeKey: 'Entity Type',
              attributeValue: 'Australian Private Company',
              valueType: 'string',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-004`,
              attributeKey: 'GST Registered',
              attributeValue: 'true',
              valueType: 'boolean',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-005`,
              attributeKey: 'GST Registration Date',
              attributeValue: '2018-06-15',
              valueType: 'date',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-006`,
              attributeKey: 'Main Business Location',
              attributeValue: 'VIC',
              valueType: 'string',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-007`,
              attributeKey: 'ANZSIC Division',
              attributeValue: 'Wholesale Trade',
              valueType: 'string',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-001-008`,
              attributeKey: 'DGR Endorsed',
              attributeValue: 'false',
              valueType: 'boolean',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            },
            // Historical attribute example
            {
              registrationAttributeId: `attr-${vendorIndex}-001-009`,
              attributeKey: 'Trading Name',
              attributeValue: 'MOS Office Supplies',
              valueType: 'string',
              source: 'ABR',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: new Date(2022, 11, 31).toISOString(),
              retrievedAt: new Date(2023, 0, 5).toISOString()
            }]
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: '624753556',
            schemeCode: 'ACN',
            issuer: 'Australian Securities and Investments Commission',
            country: 'AU',
            countryId: 'country-AU',
            countryName: 'Australia',
            effectiveFrom: new Date(2018, 5, 15).toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: new Date(2018, 5, 15).toISOString(),
            createdById: 'system',
            attributes: [{
              registrationAttributeId: `attr-${vendorIndex}-002-001`,
              attributeKey: 'Company Name',
              attributeValue: 'Melbourne Office Solutions Pty Ltd',
              valueType: 'string',
              source: 'ASIC',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-002-002`,
              attributeKey: 'Company Type',
              attributeValue: 'Proprietary Limited Company',
              valueType: 'string',
              source: 'ASIC',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-002-003`,
              attributeKey: 'Registration Date',
              attributeValue: '2018-06-15',
              valueType: 'date',
              source: 'ASIC',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-002-004`,
              attributeKey: 'Company Status',
              attributeValue: 'Registered',
              valueType: 'string',
              source: 'ASIC',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }, {
              registrationAttributeId: `attr-${vendorIndex}-002-005`,
              attributeKey: 'Registered Office State',
              attributeValue: 'Victoria',
              valueType: 'string',
              source: 'ASIC',
              effectiveFrom: new Date(2018, 5, 15).toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }]
          });
          break;
        case 'Canada':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(123456789 + vendorIndex * 111).padStart(9, '0')}RT0001`,
            schemeCode: 'BN',
            issuer: 'Canada Revenue Agency',
            country: 'CA',
            countryId: 'country-CA',
            countryName: 'Canada',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: [{
              registrationAttributeId: `attr-${vendorIndex}-001-001`,
              attributeKey: 'Business Type',
              attributeValue: 'Corporation',
              valueType: 'string',
              source: 'CRA',
              effectiveFrom: baseDate.toISOString(),
              effectiveTo: null,
              retrievedAt: new Date(2024, 0, 15).toISOString()
            }]
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `${String(123456789 + vendorIndex * 111).padStart(9, '0')}`,
            schemeCode: 'GST',
            issuer: 'Canada Revenue Agency',
            country: 'CA',
            countryId: 'country-CA',
            countryName: 'Canada',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        case 'United Kingdom':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(1234567 + vendorIndex * 111).padStart(8, '0')}`,
            schemeCode: 'CRN',
            issuer: 'Companies House',
            country: 'GB',
            countryId: 'country-GB',
            countryName: 'United Kingdom',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `GB${String(123456789 + vendorIndex * 111).padStart(9, '0')}`,
            schemeCode: 'VAT',
            issuer: 'HM Revenue & Customs',
            country: 'GB',
            countryId: 'country-GB',
            countryName: 'United Kingdom',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        case 'Germany':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `HRB ${String(12345 + vendorIndex * 111)}`,
            schemeCode: 'HRB',
            issuer: 'Handelsregister',
            country: 'DE',
            countryId: 'country-DE',
            countryName: 'Germany',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `DE${String(123456789 + vendorIndex * 111).padStart(9, '0')}`,
            schemeCode: 'VAT',
            issuer: 'Bundeszentralamt für Steuern',
            country: 'DE',
            countryId: 'country-DE',
            countryName: 'Germany',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        case 'France':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(12345678901234 + vendorIndex * 11111).padStart(14, '0')}`,
            schemeCode: 'SIRET',
            issuer: 'INSEE',
            country: 'FR',
            countryId: 'country-FR',
            countryName: 'France',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `FR${String(12345678901 + vendorIndex * 111).padStart(11, '0')}`,
            schemeCode: 'VAT',
            issuer: 'Direction générale des Finances publiques',
            country: 'FR',
            countryId: 'country-FR',
            countryName: 'France',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        case 'Japan':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(1234567890123 + vendorIndex * 11111).padStart(13, '0')}`,
            schemeCode: 'CN',
            issuer: 'National Tax Agency',
            country: 'JP',
            countryId: 'country-JP',
            countryName: 'Japan',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `T${String(1234567890123 + vendorIndex * 11111).padStart(13, '0')}`,
            schemeCode: 'JCT',
            issuer: 'National Tax Agency',
            country: 'JP',
            countryId: 'country-JP',
            countryName: 'Japan',
            effectiveFrom: new Date(2023, 9, 1).toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        case 'China':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(91110000123456789 + vendorIndex * 111).padStart(18, '0')}X`,
            schemeCode: 'USCC',
            issuer: 'State Administration for Market Regulation',
            country: 'CN',
            countryId: 'country-CN',
            countryName: 'China',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `${String(110000123456789 + vendorIndex * 111).padStart(15, '0')}`,
            schemeCode: 'TIN',
            issuer: 'State Taxation Administration',
            country: 'CN',
            countryId: 'country-CN',
            countryName: 'China',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        case 'Italy':
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(12345678901 + vendorIndex * 111).padStart(11, '0')}`,
            schemeCode: 'CF',
            issuer: 'Agenzia delle Entrate',
            country: 'IT',
            countryId: 'country-IT',
            countryName: 'Italy',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          }, {
            registrationId: `reg-${vendorIndex}-002`,
            identifier: `IT${String(12345678901 + vendorIndex * 111).padStart(11, '0')}`,
            schemeCode: 'VAT',
            issuer: 'Agenzia delle Entrate',
            country: 'IT',
            countryId: 'country-IT',
            countryName: 'Italy',
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
          break;
        default:
          // Generic fallback
          registrations.push({
            registrationId: `reg-${vendorIndex}-001`,
            identifier: `${String(123456789 + vendorIndex * 111).padStart(9, '0')}`,
            schemeCode: 'TIN',
            issuer: 'Tax Authority',
            country: country.substring(0, 2).toUpperCase(),
            countryId: `country-${country.substring(0, 2).toUpperCase()}`,
            countryName: country,
            effectiveFrom: baseDate.toISOString(),
            effectiveTo: null,
            isActive: true,
            createdAt: baseDate.toISOString(),
            createdById: 'system',
            attributes: []
          });
      }
      return registrations;
    };
    // Helper function to generate vendor payment terms
    const generateVendorPaymentTerms = (vendorIndex, vendorName) => {
      const sourceSystems = ['SAP', 'Oracle', 'Xero', 'MYOB', 'NetSuite', 'QuickBooks'];
      const sourceSystem = sourceSystems[vendorIndex % sourceSystems.length];
      // Single payment term per vendor
      const termCode = ['NET30', 'NET60', 'NET45', 'EOM', '2/10NET30', 'EOM30', 'COD'][vendorIndex % 7];
      let description = '';
      let dueDays = null;
      let discountPercent = null;
      let discountDays = null;
      let calculationBasis = 'InvoiceDate';
      let isEndOfMonth = false;
      let daysAfterEndOfMonth = null;
      switch (termCode) {
        case 'NET30':
          description = 'Net 30 Days';
          dueDays = 30;
          calculationBasis = 'InvoiceDate';
          break;
        case 'NET60':
          description = 'Net 60 Days';
          dueDays = 60;
          calculationBasis = 'InvoiceDate';
          break;
        case 'NET45':
          description = 'Net 45 Days';
          dueDays = 45;
          calculationBasis = 'InvoiceDate';
          break;
        case 'EOM':
          description = 'End of Month';
          calculationBasis = 'EndOfMonth';
          isEndOfMonth = true;
          break;
        case '2/10NET30':
          description = '2% 10 Net 30';
          dueDays = 30;
          calculationBasis = 'InvoiceDate';
          discountPercent = 2.0;
          discountDays = 10;
          break;
        case 'EOM30':
          description = 'End of Month + 30 Days';
          calculationBasis = 'EndOfMonth';
          isEndOfMonth = true;
          daysAfterEndOfMonth = 30;
          break;
        case 'COD':
          description = 'Cash on Delivery';
          calculationBasis = 'Immediate';
          break;
      }
      return {
        vendorId: `vendor-${String(vendorIndex + 1).padStart(3, '0')}`,
        paymentTermCode: termCode,
        paymentTermDescription: description,
        calculationBasis: calculationBasis,
        dueDays: dueDays,
        isEndOfMonth: isEndOfMonth,
        daysAfterEndOfMonth: daysAfterEndOfMonth,
        discountPercent: discountPercent,
        discountDays: discountDays,
        sourceSystem: sourceSystem,
        retrievedAt: new Date(2023, vendorIndex % 12, vendorIndex % 28 + 1).toISOString()
      };
    };
    return vendorNames.map((vendor, index) => {
      const statusIndex = index < 12 ? 0 : index < 15 ? 1 : index < 18 ? 3 : 2;
      const hasCompliance = index === 1 || index === 2 || index === 7 || index === 11 || index === 14;
      const orgUnit = vendor.orgUnitId ? orgUnits.find(ou => ou.id === vendor.orgUnitId) : null;
      const baseVendor = {
        vendorId: `vendor-${String(index + 1).padStart(3, '0')}`,
        vendorName: vendor.name,
        vendorCode: vendor.code,
        vendorStatusId: vendorStatuses[statusIndex].id,
        vendorStatus: vendorStatuses[statusIndex],
        orgUnitId: vendor.orgUnitId,
        orgUnit: orgUnit,
        includeChildOrgUnits: vendor.includeChildren,
        preferredBankDetailId: index % 5 === 0 ? null : 'bank-001',
        requireVerifiedBankDetailsForPayment: true,
        isActive: statusIndex === 0,
        isDeleted: false,
        createdUtc: new Date(2023, index % 12, index % 28 + 1).toISOString(),
        activatedUtc: statusIndex === 0 ? new Date(2023, index % 12, index % 28 + 1).toISOString() : null,
        suspendedUtc: null,
        deletedUtc: null,
        type: vendor.type,
        country: vendor.country,
        addresses: [],
        contactIds: vendor.contactIds || [],
        bankDetails: [],
        bankAccountIds: vendor.bankAccountIds || [],
        addressIds: vendor.addressIds || [],
        registrations: generateRegistrations(vendor.country, index),
        vendorPaymentTerms: index === 0 ? null : generateVendorPaymentTerms(index, vendor.name),
        documents: [],
        complianceIssues: [],
        customFields: {}
      };
      // Add documents (2-5 per vendor, some expired)
      const docCount = 2 + index % 4;
      const docTypes = ['Insurance', 'Tax Document', 'Business Registration', 'Certificate of Good Standing', 'W-9 Form'];
      for (let i = 0; i < docCount; i++) {
        const isExpired = hasCompliance && i === 0;
        const isPending = hasCompliance && i === 1;
        baseVendor.documents.push({
          id: `doc-${i + 1}`,
          name: `${docTypes[i % 5]} Certificate`,
          type: docTypes[i % 5],
          uploadDate: new Date(2023, index % 12, index % 28 + 1).toISOString().split('T')[0],
          expiryDate: i < 2 ? new Date(isExpired ? 2023 : 2024, 10, 10).toISOString().split('T')[0] : null,
          status: isExpired ? 'Expired' : isPending ? 'Pending Review' : 'Valid',
          fileUrl: `/documents/${vendor.code.toLowerCase()}-doc${i + 1}.pdf`
        });
      }
      // Add compliance issues for specific vendors
      if (hasCompliance) {
        if (index === 1) {
          baseVendor.complianceIssues.push({
            complianceIssueId: 'issue-001',
            vendorId: baseVendor.vendorId,
            complianceIssueTypeId: 'type-002',
            complianceIssueType: complianceIssueTypes[1],
            complianceSeverityId: 'sev-003',
            complianceSeverity: complianceSeverities[2],
            complianceIssueStatusId: 'status-001',
            complianceIssueStatus: complianceIssueStatuses[0],
            sourceSystem: 'Internal',
            sourceReference: 'INS-2023-001',
            notes: 'Insurance certificate expired on 2023-10-10',
            createdUtc: '2023-10-11T00:00:00Z',
            resolvedUtc: null
          });
        }
        if (index === 2 || index === 11) {
          baseVendor.complianceIssues.push({
            complianceIssueId: `issue-${index}`,
            vendorId: baseVendor.vendorId,
            complianceIssueTypeId: 'type-003',
            complianceIssueType: complianceIssueTypes[2],
            complianceSeverityId: 'sev-004',
            complianceSeverity: complianceSeverities[3],
            complianceIssueStatusId: 'status-001',
            complianceIssueStatus: complianceIssueStatuses[0],
            sourceSystem: 'Internal',
            sourceReference: null,
            notes: 'Missing required insurance documentation',
            createdUtc: new Date(2023, 9, 30).toISOString(),
            resolvedUtc: null
          });
        }
        if (index === 7 || index === 14) {
          baseVendor.complianceIssues.push({
            complianceIssueId: `issue-${index}`,
            vendorId: baseVendor.vendorId,
            complianceIssueTypeId: 'type-001',
            complianceIssueType: complianceIssueTypes[0],
            complianceSeverityId: 'sev-003',
            complianceSeverity: complianceSeverities[2],
            complianceIssueStatusId: 'status-001',
            complianceIssueStatus: complianceIssueStatuses[0],
            sourceSystem: 'Eftsure',
            sourceReference: `EFT-2023-${index}`,
            notes: 'Bank account verification failed',
            createdUtc: new Date(2023, 9, 30).toISOString(),
            resolvedUtc: null
          });
        }
      }
      baseVendor.isCompliant = !baseVendor.complianceIssues.some(i => i.complianceIssueStatus.code === 'OPEN' && i.complianceSeverity.isBlocking);
      // Add vendor accounts from shared data
      const vendorId = `vendor-${String(index + 1).padStart(3, '0')}`;
      baseVendor.accounts = sampleVendorAccounts[vendorId] || [];
      return baseVendor;
    });
  };
  const [vendors, setVendors] = useState(generateVendors());
  const handleAddVendor = vendorData => {
    const newVendor = {
      vendorId: `vendor-${String(vendors.length + 1).padStart(3, '0')}`,
      vendorName: vendorData.name,
      vendorCode: vendorData.code || `VEN-${String(vendors.length + 1).padStart(3, '0')}`,
      vendorStatusId: 'status-002',
      vendorStatus: vendorStatuses[1],
      orgUnitId: vendorData.orgUnitId || null,
      orgUnit: vendorData.orgUnitId ? orgUnits.find(ou => ou.id === vendorData.orgUnitId) : null,
      includeChildOrgUnits: vendorData.includeChildOrgUnits || false,
      paymentTermId: vendorData.paymentTermId || 'term-003',
      paymentTerm: paymentTerms.find(t => t.id === (vendorData.paymentTermId || 'term-003')),
      preferredBankDetailId: null,
      requireVerifiedBankDetailsForPayment: true,
      isActive: true,
      isDeleted: false,
      createdUtc: new Date().toISOString(),
      activatedUtc: null,
      suspendedUtc: null,
      deletedUtc: null,
      type: vendorData.type || '',
      country: vendorData.country || '',
      addresses: [],
      contactIds: [],
      bankDetails: [],
      bankAccountIds: [],
      addressIds: [],
      registrations: [],
      documents: [],
      complianceIssues: [],
      customFields: {},
      get isCompliant() {
        return !this.complianceIssues.some(i => i.complianceIssueStatus.code === 'OPEN' && i.complianceSeverity.isBlocking);
      }
    };
    setVendors([...vendors, newVendor]);
    setCurrentView('vendors');
  };
  const handleUpdateVendor = updatedVendor => {
    setVendors(vendors.map(vendor => vendor.vendorId === updatedVendor.vendorId ? updatedVendor : vendor));
    setSelectedVendor(null);
    setCurrentView('vendors');
  };
  const handleSendOnboardingInvite = vendorData => {
    console.log('Sending onboarding invite to:', vendorData.email);
    const newVendor = {
      vendorId: `vendor-${String(vendors.length + 1).padStart(3, '0')}`,
      vendorName: vendorData.name,
      vendorCode: `VEN-${String(vendors.length + 1).padStart(3, '0')}`,
      vendorStatusId: 'status-004',
      vendorStatus: vendorStatuses[3],
      orgUnitId: vendorData.orgUnitId || null,
      orgUnit: vendorData.orgUnitId ? orgUnits.find(ou => ou.id === vendorData.orgUnitId) : null,
      includeChildOrgUnits: vendorData.includeChildOrgUnits || false,
      paymentTermId: 'term-003',
      paymentTerm: paymentTerms[2],
      preferredBankDetailId: null,
      requireVerifiedBankDetailsForPayment: true,
      isActive: true,
      isDeleted: false,
      createdUtc: new Date().toISOString(),
      activatedUtc: null,
      suspendedUtc: null,
      deletedUtc: null,
      type: vendorData.type || '',
      country: vendorData.country || '',
      addresses: [],
      contactIds: [],
      bankDetails: [],
      bankAccountIds: [],
      addressIds: [],
      registrations: [],
      documents: [],
      complianceIssues: [],
      customFields: {},
      get isCompliant() {
        return !this.complianceIssues.some(i => i.complianceIssueStatus.code === 'OPEN' && i.complianceSeverity.isBlocking);
      }
    };
    setVendors([...vendors, newVendor]);
    setCurrentView('vendors');
  };
  const handleViewVendor = vendor => {
    if (!vendor) return;
    setSelectedVendor(vendor);
    setCurrentView('vendor-details');
  };
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
      case 'vendor-management':
        return <VendorDashboard vendors={vendors} setCurrentView={setCurrentView} locale={locale} data-id="element-22660" />;
      case 'vendors':
        return <VendorList vendors={vendors} onViewVendor={handleViewVendor} setCurrentView={setCurrentView} locale={locale} data-id="element-22661" />;
      case 'onboard-vendor':
        return <VendorOnboarding onSave={handleAddVendor} onSendInvite={handleSendOnboardingInvite} setCurrentView={setCurrentView} paymentTerms={paymentTerms} orgUnits={orgUnits} locale={locale} data-id="element-22662" />;
      case 'vendor-details':
        return selectedVendor ? <VendorDetails vendor={selectedVendor} onUpdate={handleUpdateVendor} setCurrentView={setCurrentView} paymentTerms={paymentTerms} vendorStatuses={vendorStatuses} orgUnits={orgUnits} locale={locale} data-id="element-22663" /> : <div className="text-center py-8" data-id="element-22664">
            <p className="text-gray-500" data-id="element-22665">
              No vendor selected. Please select a vendor from the list.
            </p>
            <button onClick={() => setCurrentView('vendors')} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22666">
              View Vendors
            </button>
          </div>;
      case 'compliance-check':
        return <ComplianceCheck vendor={selectedVendor} onUpdate={handleUpdateVendor} setCurrentView={setCurrentView} locale={locale} data-id="element-22667" />;
      default:
        return <VendorDashboard vendors={vendors} setCurrentView={setCurrentView} locale={locale} data-id="element-22668" />;
    }
  };
  return <div className="space-y-6" data-id="element-22669">
      <div className="flex justify-between items-center" data-id="element-22670">
        <div data-id="element-22671">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-22672">
            Vendor Management
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-22673">
            Manage vendor profiles, compliance, and onboarding
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-22674">
          <button onClick={() => setShowLookup(!showLookup)} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22675">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-22676">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" data-id="element-22677"></path>
            </svg>
            Lookup
          </button>
        </div>
      </div>

      {renderContent()}

      <VendorLookupModal isOpen={showLookup} onClose={() => setShowLookup(false)} onLookup={vendor => {
      handleViewVendor(vendor);
      setShowLookup(false);
    }} data-id="element-22678" />
    </div>;
}